import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select'
import { createPopper } from '@popperjs/core'

// Constants
const HEADER_BUFFER = 100
const FOOTER_SELECTOR = '.dashboard-form-footer'
const PADDED_MODIFIERS = new Set(['flip', 'preventOverflow'])

/**
 * Unified Tom Select controller - drop-in replacement for select2_controller
 *
 * Supports all data attributes from the original select2_controller:
 * - data-resource-url: AJAX endpoint for loading options
 * - data-resource-name: resource name for tag creation (e.g., 'brand')
 * - data-resource-filters: dynamic filters object
 * - data-resource-prefix: filter prefix for nested params
 * - data-resource-parameter: parameter name (default: 'name')
 * - data-tags: enable tag mode with ++ creation shorthand
 * - data-freeform: allow arbitrary text input
 * - data-token-separators: array of separator characters for tags
 */
export default class extends Controller {
  connect() {
    // Prevent double initialization
    if (this.element.tomselect) {
      return
    }

    const config = this.buildConfig()
    this.tomselect = new TomSelect(this.element, config)
    this.setupPopper()
    this.setupEventForwarding()
    this.setupPlaceholder()
    this.setupPreventScrollOnFocus()
    this.setupDisabledListener()
  }

  // Prevent Chrome from scrolling when focusing the dropdown input
  setupPreventScrollOnFocus() {
    const dropdownInput = this.tomselect.dropdown.querySelector('.dropdown-input')
    if (dropdownInput) {
      const originalFocus = dropdownInput.focus.bind(dropdownInput)
      dropdownInput.focus = options => {
        originalFocus({ preventScroll: true, ...options })
      }
    }
  }

  // Sync Tom Select disabled state when toggle controller changes it
  setupDisabledListener() {
    this.handleDisabledChange = event => {
      if (this.tomselect) {
        if (event.detail.disabled) {
          this.tomselect.disable()
        } else {
          this.tomselect.enable()
        }
      }
    }
    this.element.addEventListener('toggle:disabled', this.handleDisabledChange)
  }

  setupPlaceholder() {
    const placeholder = this.element.getAttribute('placeholder')
    if (!placeholder) return

    // Skip custom placeholder for AJAX and freeform modes - Tom Select handles it natively
    if (this.getData('resourceUrl') || this.getData('freeform')) return

    // Create placeholder element
    this.placeholderEl = document.createElement('div')
    this.placeholderEl.className = 'ts-placeholder'
    this.placeholderEl.textContent = placeholder
    this.tomselect.control.appendChild(this.placeholderEl)

    // Update placeholder visibility based on selection
    this.updatePlaceholder()

    // Listen for changes to update placeholder
    this.tomselect.on('change', () => this.updatePlaceholder())
    this.tomselect.on('item_add', () => this.updatePlaceholder())
    this.tomselect.on('item_remove', () => this.updatePlaceholder())
    this.tomselect.on('clear', () => this.updatePlaceholder())
  }

  updatePlaceholder() {
    if (!this.placeholderEl) return
    const hasValue = this.tomselect.getValue() && this.tomselect.getValue().length > 0
    this.placeholderEl.style.display = hasValue ? 'none' : 'block'
  }

  disconnect() {
    if (this.handleDisabledChange) {
      this.element.removeEventListener('toggle:disabled', this.handleDisabledChange)
    }
    if (this.popper) {
      this.popper.destroy()
      this.popper = null
    }
    if (this.tomselect) {
      this.tomselect.destroy()
      this.tomselect = null
    }
  }

  buildConfig() {
    const placeholder = this.element.getAttribute('placeholder')

    const config = {
      plugins: this.getPlugins(),
      placeholder: placeholder,
      allowEmptyOption: true,
      maxOptions: null, // show all options
      dropdownParent: 'body', // Render dropdown in body to escape overflow containers
      closeAfterSelect: !this.element.multiple, // Keep dropdown open for multi-select
      render: {
        no_results: () => {
          return '<div class="no-results" style="padding: 0.625rem 1rem; text-align: center; color: #6b7280; background: white; border-radius: 0 0 0.75rem 0.75rem;">No results found</div>'
        },
      },
    }
    const dropdownClass = this.getData('dropdownClass')
    if (dropdownClass) {
      config.dropdownClass = dropdownClass.includes('ts-dropdown') ? dropdownClass : `ts-dropdown ${dropdownClass}`
    }
    if (this.getData('noControlInput') !== null) {
      config.controlInput = null
      config.searchField = []
    }
    if (this.getData('hideEmptyOption') !== null) {
      config.render.option = (data, escape) => {
        if (!data.value) return '<div class="hidden"></div>'
        return `<div>${escape(data.text)}</div>`
      }
    }

    // Tags mode (multi-select with creation)
    if (this.getData('tags')) {
      config.create = false // We handle creation via ++ shorthand
      config.persist = false
    }

    // Freeform mode - allow arbitrary text input
    if (this.getData('freeform')) {
      config.create = true
      config.persist = true
      // Don't show "Add" option if an option already exists (case-insensitive)
      config.createFilter = input => {
        if (!this.tomselect) return true
        const inputLower = input.toLowerCase()
        const options = Object.values(this.tomselect.options || {})
        return !options.some(opt => {
          const text = opt.text || opt.value || ''
          return text.toLowerCase() === inputLower
        })
      }
    }

    // Token separators for tags
    const tokenSeparators = this.getData('tokenSeparators')
    if (tokenSeparators) {
      config.delimiter = Array.isArray(tokenSeparators) ? tokenSeparators[0] : tokenSeparators
    }

    // AJAX loading
    if (this.getData('resourceUrl')) {
      config.valueField = 'id'
      config.labelField = 'text'
      config.searchField = 'text'
      config.load = (query, callback) => this.loadOptions(query, callback)

      // If tags mode is enabled with AJAX, show creation hint and handle ++ shorthand
      if (this.getData('tags')) {
        config.render = {
          ...config.render,
          no_results: () => {
            return '<div class="no-results" style="padding: 0.625rem 1rem; text-align: center; color: #6b7280; background: white; border-radius: 0 0 0.75rem 0.75rem;">No results found. Type \'++\' at the end to create.</div>'
          },
        }
        config.load = (query, callback) => this.loadOptionsWithTagCreation(query, callback)
      }
    }

    return config
  }

  getPlugins() {
    const plugins = {}
    const placeholder = this.element.getAttribute('placeholder')

    // Add clear button if placeholder is set (same behavior as select2 allowClear)
    // Skip if data-no-clear is set (e.g., for required fields)
    if (placeholder && this.getData('noClear') === null) {
      plugins.clear_button = {
        title: 'Clear selection',
      }
    }

    // Add remove button for multi-select
    if (this.element.multiple) {
      plugins.remove_button = {
        title: 'Remove',
      }
    }

    // Only enable the search plugin when `data-no-search` is not present on the element.
    if (this.getData('noSearch') === null) {
      plugins.dropdown_input = {}
    }

    return plugins
  }

  setupPopper() {
    const padding = this.viewportPadding()

    // Create popper instance for positioning dropdown
    this.popper = createPopper(this.tomselect.control, this.tomselect.dropdown, {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, -1], // Slight negative offset to remove gap
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top-start'],
            padding,
            rootBoundary: 'viewport',
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding,
          },
        },
        {
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`
          },
          effect: ({ state }) => {
            state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`
          },
        },
      ],
    })

    // Set z-index on dropdown (lower than header so it scrolls behind)
    this.tomselect.dropdown.style.zIndex = '100'
  }

  viewportPadding() {
    const controlInset = this.controlInset()
    return { top: HEADER_BUFFER + controlInset, bottom: this.viewportInset(FOOTER_SELECTOR) + controlInset }
  }

  controlInset() {
    return this.tomselect?.control?.offsetHeight || 0
  }

  viewportInset(selector) {
    return [...document.querySelectorAll(selector)].reduce((maxInset, element) => {
      return Math.max(maxInset, this.elementViewportInset(element))
    }, 0)
  }

  elementViewportInset(element) {
    const rect = element.getBoundingClientRect()
    const style = getComputedStyle(element)

    if (rect.width === 0 || rect.height === 0 || style.display === 'none' || style.visibility === 'hidden') {
      return 0
    }

    return Math.max(0, window.innerHeight - rect.top)
  }

  updatePopperPosition() {
    if (!this.popper) {
      this.applyDropdownStyling()
      return
    }

    const padding = this.viewportPadding()
    this.popper
      .setOptions(options => ({
        ...options,
        modifiers: options.modifiers.map(modifier => {
          if (PADDED_MODIFIERS.has(modifier.name)) {
            return {
              ...modifier,
              options: {
                ...modifier.options,
                padding,
              },
            }
          }

          return modifier
        }),
      }))
      .then(() => {
        this.applyDropdownStyling()
      })
  }

  applyDropdownStyling() {
    if (!this.tomselect.dropdown) return

    const placement = this.tomselect.dropdown.getAttribute('data-popper-placement') || 'bottom-start'
    const isTop = placement.startsWith('top')

    if (isTop) {
      // Opening upward: add class for CSS to handle styling
      this.tomselect.wrapper.classList.add('dropdown-up')
      this.tomselect.dropdown.classList.add('dropdown-up')
      this.tomselect.dropdown.style.borderRadius = '0.75rem 0.75rem 0 0'
      this.tomselect.dropdown.style.borderBottom = 'none'
      this.tomselect.dropdown.style.borderTop = ''
    } else {
      // Opening downward: remove class, CSS handles default styling
      this.tomselect.wrapper.classList.remove('dropdown-up')
      this.tomselect.dropdown.classList.remove('dropdown-up')
      this.tomselect.dropdown.style.borderRadius = '0 0 0.75rem 0.75rem'
      this.tomselect.dropdown.style.borderTop = 'none'
      this.tomselect.dropdown.style.borderBottom = ''
    }
  }

  setupEventForwarding() {
    // Handle scroll - update popper position and close if out of view
    this.scrollHandler = () => {
      if (this.tomselect.isOpen) {
        const rect = this.element.getBoundingClientRect()

        // Close dropdown if input has scrolled above the visible area
        if (rect.top < HEADER_BUFFER) {
          this.tomselect.close()
          return
        }

        // Update popper position and re-apply styling in case placement changed
        if (this.popper) {
          this.updatePopperPosition()
        }
      }
    }

    // Forward Tom Select events as native DOM events for Stimulus compatibility
    this.tomselect.on('change', () => {
      // Copy any custom data attributes from the selected option's data to the option element
      const selectedValue = this.tomselect.getValue()
      if (selectedValue && this.tomselect.options[selectedValue]) {
        const optionData = this.tomselect.options[selectedValue]
        const optionEl = this.element.options[this.element.selectedIndex]

        if (optionEl && optionEl.value === selectedValue) {
          Object.entries(optionData).forEach(([key, value]) => {
            if (key.startsWith('data-')) {
              const attrValue = typeof value === 'object' ? JSON.stringify(value) : value
              optionEl.setAttribute(key, attrValue)
            }
          })
        }
      }

      this.element.dispatchEvent(new Event('change', { bubbles: true }))
    })

    this.tomselect.on('clear', () => {
      this.element.dispatchEvent(new Event('clear', { bubbles: true }))
    })

    // Update Popper when items are added/removed (control height changes)
    this.tomselect.on('item_add', () => {
      if (this.popper && this.tomselect.isOpen) {
        this.updatePopperPosition()
      }
    })

    this.tomselect.on('item_remove', () => {
      if (this.popper && this.tomselect.isOpen) {
        this.updatePopperPosition()
      }
    })

    this.tomselect.on('dropdown_open', () => {
      // Always load fresh data on dropdown open (no caching)
      if (this.getData('resourceUrl')) {
        this.tomselect.clearOptions()
        this.tomselect.load('')
      }

      // Update popper position and then apply styling based on placement
      this.updatePopperPosition()

      // Find all scrollable ancestors and listen for scroll
      this.scrollableAncestors = []
      let el = this.element.parentElement
      while (el) {
        const style = getComputedStyle(el)
        if (
          style.overflow === 'auto' ||
          style.overflow === 'scroll' ||
          style.overflowY === 'auto' ||
          style.overflowY === 'scroll'
        ) {
          el.addEventListener('scroll', this.scrollHandler)
          this.scrollableAncestors.push(el)
        }
        el = el.parentElement
      }
      window.addEventListener('scroll', this.scrollHandler)

      this.element.dispatchEvent(new Event('opening', { bubbles: true }))
    })

    this.tomselect.on('dropdown_close', () => {
      // Remove scroll listeners
      if (this.scrollableAncestors) {
        this.scrollableAncestors.forEach(el => {
          el.removeEventListener('scroll', this.scrollHandler)
        })
        this.scrollableAncestors = []
      }
      window.removeEventListener('scroll', this.scrollHandler)

      // Remove dropdown-up class
      if (this.tomselect.wrapper) {
        this.tomselect.wrapper.classList.remove('dropdown-up')
      }
    })
  }

  // Data attribute getters
  getData(attr) {
    const attrName = 'data-' + attr.replace(/([A-Z])/g, '-$1').toLowerCase()
    return this.element.getAttribute(attrName)
  }

  getDataJSON(attr) {
    const value = this.getData(attr)
    if (!value) return null
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }

  get userFilters() {
    const rawFilters = this.getData('resourceFilters')
    if (!rawFilters || rawFilters === 'undefined') {
      return null
    }
    return this.getDataJSON('resourceFilters')
  }

  get prefixedFilters() {
    const userFilters = this.userFilters
    if (!userFilters) {
      return null
    }

    const filterPrefixes = this.getData('resourcePrefix')
    if (filterPrefixes) {
      const prefixedFilters = {}
      prefixedFilters[filterPrefixes] = userFilters
      return prefixedFilters
    }

    return userFilters
  }

  // AJAX loading
  async loadOptions(query, callback) {
    try {
      const url = new URL(this.getData('resourceUrl'), window.location.origin)
      const params = this.buildQueryParams(query)
      this.appendParams(url, params)

      const response = await fetch(url.toString(), {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      const results = data.results || data
      callback(results)
    } catch (error) {
      console.error('Tom Select load error:', error)
      callback()
    }
  }

  async loadOptionsWithTagCreation(query, callback) {
    const shorthand = '++'

    if (query.endsWith(shorthand) && query.length > shorthand.length) {
      const name = query.slice(0, -shorthand.length)
      await this.createTag(name)
      callback()
      return
    }

    return this.loadOptions(query, callback)
  }

  buildQueryParams(query) {
    const params = {
      term: String(query || ''),
      formatting: 'select2',
    }

    const userFilters = this.prefixedFilters
    if (userFilters) {
      params.filters = userFilters
    }

    return params
  }

  appendParams(url, params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        return
      }
      if (typeof value === 'object' && value !== null) {
        this.appendNestedParams(url, key, value)
      } else {
        url.searchParams.append(key, value)
      }
    })
  }

  appendNestedParams(url, prefix, obj) {
    Object.entries(obj).forEach(([key, value]) => {
      const paramKey = `${prefix}[${key}]`
      if (typeof value === 'object' && value !== null) {
        this.appendNestedParams(url, paramKey, value)
      } else {
        url.searchParams.append(paramKey, value)
      }
    })
  }

  async createTag(name) {
    const url = this.getData('resourceUrl')
    const resourceName = this.getData('resourceName')
    const parameterName = this.getData('resourceParameter') || 'name'

    const params = {
      formatting: 'select2',
    }
    params[resourceName] = {
      [parameterName]: name,
      ...(this.userFilters || {}),
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.csrfToken,
        },
        body: JSON.stringify(params),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.createTagSuccess(data)
    } catch (error) {
      console.error('Tag creation error:', error)
      alert(`Error creating tag: ${error.message}`)
    }
  }

  createTagSuccess(response) {
    this.tomselect.addOption({
      id: response.id,
      text: response.text,
      ...response,
    })
    this.tomselect.setValue(response.id)
    this.tomselect.close()
    this.element.dispatchEvent(new Event('change', { bubbles: true }))
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute('content') : ''
  }

  // Public API methods
  clear() {
    if (this.tomselect) {
      this.tomselect.clear()
    }
  }

  setValue(value) {
    if (this.tomselect) {
      this.tomselect.setValue(value)
    }
  }

  getValue() {
    if (this.tomselect) {
      return this.tomselect.getValue()
    }
    return null
  }
}
